import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import { searchErrorHandler } from "utils/formValidation";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
} from "utils/utils";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import Datepicker from "components/Input/Datepicker/Datepicker";
import Selection from "components/Input/Selection/Selection";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import paymentGatewayOptions from "utils/paymentGatewayOptions/PaymentGatewayOptions.jsx";

import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import s from "./SalesAchievement.module.scss";
import { exportCsv, queryList, querySales } from "./model";

const SalesAchievement = ({ path }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "salesAchievementGrid";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [salesList, setSalesList] = useState([]);
  const [searchFieldInput, setSearchFieldInput] = useState({
    startDate: null,
    endDate: null,
    paymentGateway: "",
    currency: "",
    sales: "",
  });
  const [focus, setFocus] = useState({});
  const [error, setError] = useState({
    startDate: false,
    endDate: false,
    startDateLaterThanEndDate: false,
  });

  const [columnsArray, setColumnsArray] = useState([]);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse === undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await getSales();
      await initialFetch();
    };
    init();
  }, []);

  const initialFetch = async () => {
    setLoading(true);
    const response = await queryList({
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(t("general.message.initial.fetch.error"), "error");
    } else if (result?.responseCode === "0000") {
      if (result.data.length === 0) {
        snackbar(t("general.message.no.result"), "warning");
      }
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(false);
    setLoading(false);
  };

  const getSales = async () => {
    const response = await querySales({});
    const result = response?.data;
    if (result && result.data && Array.isArray(result.data)) {
      const newSalesList = [];
      result.data.forEach((sales) => {
        newSalesList.push({
          key: sales.id,
          title: sales.firstName,
          value: String(sales.id),
        });
      });
      setSalesList(newSalesList);
    } else {
      setSalesList([]);
    }
  };

  const resetInputField = () => {
    setSearchFieldInput({
      startDate: null,
      endDate: null,
      paymentGateway: "",
      currency: "",
      sales: "",
    });
  };

  const resetError = () => {
    setError({
      startDate: false,
      endDate: false,
      startDateLaterThanEndDate: false,
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    resetError();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            startDateLaterThanEndDate: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const response = await queryList(payload);
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
    } else if (result?.responseCode === "0000") {
      if (result.data.length === 0) {
        snackbar(t("general.message.no.result"), "warning");
      }
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(true);
    setSearching(false);
  };

  const handleExport = async () => {
    if (searchFieldInput.startDate && !searchFieldInput.endDate) {
      snackbar(t("general.message.export.function.error.message.2"), "warning");
      return;
    }
    if (searchFieldInput.endDate && !searchFieldInput.startDate) {
      snackbar(t("general.message.export.function.error.message.1"), "warning");
      return;
    }
    if (searchFieldInput.startDate > searchFieldInput.endDate) {
      snackbar(t("general.message.export.function.error.message.3"), "warning");
      return;
    }
    if (
      moment(searchFieldInput.endDate)
        .endOf("day")
        .diff(moment(searchFieldInput.startDate).startOf("day"), "months") > 5
    ) {
      snackbar(t("general.message.export.function.error.message.4"), "warning");
      return;
    }
    setExporting(true);
    const payload = trimObject({ ...searchFieldInput });
    const response = await exportCsv(payload);
    const result = response?.data;
    setExporting(false);
    if (result && result.responseCode === "0000") {
      snackbar(t("general.message.export.function.success.message"), "success");
      const url = result.data;
      window.location = url;
    }
    if (result && result.responseCode === "2200") {
      return snackbar(
        t("general.message.export.function.error.message.5"),
        "warning",
      );
    }
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const transactionDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startDate: false,
        startDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      setError({ ...error, endDate: false, startDateLaterThanEndDate: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    }
  };
  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const columns = [
    {
      index: 0,
      key: "paymentGateway",
      title: t("page.analytics.sales.achievement.column.title.gateway"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "currency",
      title: t(
        "page.analytics.sales.achievement.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "totalAmount",
      title: t("page.analytics.sales.achievement.column.title.total.amount"),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "sales",
      title: t("page.analytics.sales.achievement.column.title.sales"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <Datepicker
            label={t(
              "page.analytics.sales.achievement.transaction.start.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.analytics.sales.achievement.transaction.start.date.error.message.2",
                  )
                : t(
                    "page.analytics.sales.achievement.transaction.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.startDate}
            limitValue={searchFieldInput.endDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.analytics.sales.achievement.transaction.end.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.analytics.sales.achievement.transaction.end.date.error.message.2",
                  )
                : t(
                    "page.analytics.sales.achievement.transaction.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.endDate}
            limitValue={searchFieldInput.startDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError: error.endDate || error.startDateLaterThanEndDate,
            }}
          />
          <Selection
            label={t("page.analytics.sales.achievement.payment.gateway.label")}
            selectionValue={searchFieldInput.paymentGateway}
            onchangeFn={(e) => textInputOnchange(e, "paymentGateway")}
            onfocusFn={() => textInputOnFocus("paymentGateway")}
            onblurFn={() => textInputOnBlur("paymentGateway")}
            options={paymentGatewayOptions("all")}
            sxParams={{ selectionFocus: focus.paymentGateway }}
          />
          <Selection
            label={t(
              "page.analytics.sales.achievement.transaction.currency.label",
            )}
            selectionValue={searchFieldInput.currency}
            onchangeFn={(e) => textInputOnchange(e, "currency")}
            onfocusFn={() => textInputOnFocus("currency")}
            onblurFn={() => textInputOnBlur("currency")}
            options={transactionCurrencyOptions("all")}
            sxParams={{ selectionFocus: focus.currency }}
          />
          <Selection
            label={t("page.analytics.sales.achievement.sales.label")}
            selectionValue={searchFieldInput.sales}
            onchangeFn={(e) => textInputOnchange(e, "sales")}
            onfocusFn={() => textInputOnFocus("sales")}
            onblurFn={() => textInputOnBlur("sales")}
            options={salesList}
            sxParams={{ selectionFocus: focus.sales }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching || exporting}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading || searching || exporting}
          exporting={exporting}
          exportBtn
          handleExport={handleExport}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "analytics-sale-achievement",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}
          {searching && <Spin type={"search"} />}
        </div>
      </div>
    </div>
  );
};

export default React.memo(SalesAchievement);
